// Downloading survey CSVs.
//
// The CSV itself is built on the server (Services/SurveyExportRender.cs) under
// the customer's export template, so every button in the app produces the same
// shape of file. This used to be assembled by hand in three components which had
// quietly drifted apart on whether the grid correction applied, what date
// anchored it, how the azimuth wrapped and how many decimals were written.
//
// The create-anchor-click-revoke dance below was copy-pasted four times; it lives
// here now.

import i18n from '../i18n';

/**
 * Hands a blob to the browser as a download.
 */
export function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link); // required for Firefox
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

// The server names the file; this is only used if the header is missing or
// unreadable.
function filenameFrom(response, fallback) {
    const header = response.headers.get('Content-Disposition') || '';
    // filename*=UTF-8''... wins over the plain form when both are present.
    const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
    if (encoded) {
        try {
            return decodeURIComponent(encoded[1]);
        } catch {
            // Fall through to the plain form.
        }
    }
    const plain = /filename="?([^";]+)"?/i.exec(header);
    return plain ? plain[1] : fallback;
}

/**
 * Exports one or more surveys as a single CSV.
 *
 * @param {string[]} fileIds  survey fileIds - never fileNames, which are not
 *                            unique across a customer.
 * @param {string} layout     Columns | Merged | Heading | Metadata. Anything
 *                            unrecognised is read as Columns by the server.
 * @param {string} fallbackName used only if the server sends no filename.
 * @returns {Promise<void>}   resolves once the download has been handed over.
 * @throws {Error} with the server's message when the export is refused.
 */
export async function exportSurveys(fileIds, layout, fallbackName = 'survey-export.csv') {
    const response = await fetch('api/surveys/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileIds, layout })
    });
    if (!response.ok) {
        // The endpoint returns a plain-text reason for the cases a user can
        // actually act on - nothing selected, too many selected, none found.
        const message = await response.text().catch(() => '');
        throw new Error(message || i18n.t('common.exportFailed', { status: response.status }));
    }
    const blob = await response.blob();
    downloadBlob(blob, filenameFrom(response, fallbackName));
}
